/**
 * Authenticated fetches for voice (streamed speech, utterance transcription): the same bearer token and
 * staff-impersonation / dev-harness headers as the app's agent requests. Plain `fetch` because speech is
 * read as a stream (axios buffers responses).
 */
import { GUEST_MODE } from '@/guest/guestMode'
import { getDevHarnessHeaders } from '@/utils/devHarnessHeaders'
import { getImpersonationHeaders } from '@/utils/impersonationHeaders'

export function backendUrl(path: string): string {
    const base = ((import.meta.env.VITE_APP_BACKEND_URL as string | undefined) ?? '').replace(/\/$/, '')
    return `${base}${path}`
}

async function accessToken(): Promise<string | null> {
    try {
        const { supabase } = await import('@/supabase')
        return (await supabase.auth.getSession())?.data?.session?.access_token ?? null
    } catch {
        return null
    }
}

export async function voiceHeaders(extra: Record<string, string> = {}): Promise<Record<string, string>> {
    const token = await accessToken()
    return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...getImpersonationHeaders(),
        ...getDevHarnessHeaders(),
        ...extra,
    }
}

export class VoiceHttpError extends Error {
    constructor(message: string, public readonly status: number) {
        super(message)
    }
}

/** One utterance → text, through the dictation endpoint (`/agent/voice/transcribe`). */
export async function transcribeUtterance(wav: Blob, language: string, signal?: AbortSignal): Promise<string> {
    if (GUEST_MODE) throw new VoiceHttpError('Voice is not available in guest mode.', 403)
    const form = new FormData()
    form.append('file', wav, 'utterance.wav')
    form.append('language', language)
    const res = await fetch(backendUrl('/agent/voice/transcribe'), { method: 'POST', headers: await voiceHeaders(), body: form, signal })
    if (!res.ok) {
        // `{ error: <a sentence to show>, code }`
        const body = (await res.json().catch(() => ({}))) as { error?: unknown }
        throw new VoiceHttpError(typeof body.error === 'string' && body.error ? body.error : `Transcription failed (${res.status})`, res.status)
    }
    const data = (await res.json().catch(() => ({}))) as { text?: string }
    return (data.text ?? '').trim()
}
